"use client";

import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { CalendarIcon } from "lucide-react";
import {
  ComponentProps,
  Dispatch,
  SetStateAction,
  useEffect,
  useState,
} from "react";
import { formatDatePickerValue, parseDatePickerValue } from "./datePickerUtils";

interface DatePickerBaseProps {
  label?: string;
  error?: string;
  setValue: Dispatch<SetStateAction<string>>;
  minDate?: Date;
  maxDate?: Date;
  disabledDates?: ComponentProps<typeof Calendar>["disabled"];
  valueFormat?: "localized" | "iso";
}

type DatePickerProps = ComponentProps<typeof Input> & DatePickerBaseProps;

function formatIsoDateValue(date: Date | undefined): string {
  if (!date) {
    return "";
  }

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
}

function mergeDisabledDates(
  minDate: Date | undefined,
  maxDate: Date | undefined,
  disabledDates: ComponentProps<typeof Calendar>["disabled"],
): ComponentProps<typeof Calendar>["disabled"] {
  const disabled = [];

  if (minDate) {
    disabled.push({ before: minDate });
  }

  if (maxDate) {
    disabled.push({ after: maxDate });
  }

  if (Array.isArray(disabledDates)) {
    disabled.push(...disabledDates);
  } else if (disabledDates) {
    disabled.push(disabledDates);
  }

  return disabled.length > 0 ? disabled : undefined;
}

const DatePicker = ({
  disabledDates,
  maxDate,
  minDate,
  valueFormat = "localized",
  ...props
}: DatePickerProps) => {
  const [open, setOpen] = useState(false);
  const [date, setDate] = useState<Date | undefined>(
    parseDatePickerValue(String(props.value ?? "")),
  );
  const [month, setMonth] = useState<Date | undefined>(date);
  const calendarDisabled = mergeDisabledDates(minDate, maxDate, disabledDates);
  const formatSelectedDate =
    valueFormat === "iso" ? formatIsoDateValue : formatDatePickerValue;
  const inputValue = date
    ? formatDatePickerValue(date)
    : String(props.value ?? "");

  useEffect(() => {
    const parsed = parseDatePickerValue(String(props.value ?? ""));

    setDate(parsed);
    if (parsed) {
      setMonth(parsed);
    }
  }, [props.value]);

  return (
    <div className={`${props.className} flex w-full flex-col items-start`}>
      {props.label && (
        <label
          className={`${props.error ? "text-red-500" : "text-text"} text-sm`}
        >
          {props.label}
        </label>
      )}
      <div className="relative flex w-full gap-2">
        <Input
          name={props.name}
          id="date"
          value={inputValue}
          placeholder={props.placeholder ?? "DD.MM.YYYY"}
          className={`bg-background pr-10 ${props.error ? "border-red-500" : ""} `}
          disabled={props.disabled}
          onChange={(e) => {
            const parsed = parseDatePickerValue(e.target.value);
            props.setValue(
              parsed ? formatSelectedDate(parsed) : e.target.value,
            );
            setDate(parsed);
            if (parsed) {
              setMonth(parsed);
            }
            if (props.onChange) {
              props.onChange(e);
            }
          }}
          onBlur={props.onBlur}
          onKeyDown={(e) => {
            if (e.key === "ArrowDown") {
              e.preventDefault();
              setOpen(true);
            }
          }}
        />
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button
              id="date-picker"
              variant="ghost"
              className="absolute top-1/2 right-2 size-6 -translate-y-1/2"
              disabled={props.disabled}
            >
              <CalendarIcon className="size-3.5 cursor-pointer" />
              <span className="sr-only cursor-pointer">Select date</span>
            </Button>
          </PopoverTrigger>
          <PopoverContent
            className="w-auto overflow-hidden p-0"
            align="end"
            alignOffset={-8}
            sideOffset={10}
          >
            <Calendar
              mode="single"
              selected={date}
              captionLayout="dropdown"
              month={month}
              onMonthChange={setMonth}
              disabled={calendarDisabled}
              onSelect={(date) => {
                setDate(date);
                props.setValue(formatSelectedDate(date));
                setOpen(false);
              }}
            />
          </PopoverContent>
        </Popover>
      </div>
      {props.error && (
        <span className="pt-1 pl-1 text-xs text-red-500">{props.error}</span>
      )}
    </div>
  );
};

export default DatePicker;
